import React, {useEffect, useState} from "react";
import {Divider, IconButton} from "@mui/material";
import {Add, ArrowDropDown, Check, Delete} from "@mui/icons-material";
import {useStyles, VisibilityRangeSlider} from "./DrawingSection.css";
import moment from "moment";
import {useIntl} from "react-intl";
import DrawingShape from "../drawingShape/DrawingShape";

const DrawingSection = ({
                            // Init the value always with the current value stored in the reference (the onSave method will updated it)
                            initDrawing,

                            // Orientation for the drawing area
                            canvasTop,
                            canvasLeft,
                            canvasHeight,
                            canvasWidth,

                            // Start end ranges used for determining the slider range for the drawing time
                            visibilityRangeMin,
                            visibilityRangeMax,

                            // Default values within the visibility range
                            visibilityRangeStart,
                            visibilityRangeEnd,

                            // Callback handlers
                            onVisibilityRangeChange,
                            onSave,
                            onDeleteAll,
                            onLeaveDrawingMode,

                            // Toggles for showing / hiding certain elements (i.e. sliders and toolbar)
                            drawingMode,
                            previewMode,

                            // Blurring vs. Highlighting
                            mode,

                            // Optional marks to be added to the time range slider
                            timeRangeMarks = []
                        }) => {
    const classes = useStyles({
        canvasTop: canvasTop,
        canvasLeft: canvasLeft,
        canvasHeight: canvasHeight,
        canvasWidth: canvasWidth,
        drawingMode: drawingMode
    });

    // Time range when the drawing needs to be shown (holds the absolute time value of the slider)
    const [visibilityRange, setVisibilityRange] = useState([
        visibilityRangeStart,
        visibilityRangeEnd,
    ]);

    // Holds the information about all shapes to be rendered
    const [shapes, setShapes] = useState(initDrawing?.drawing || []);

    // The value of the last moved thumb - required for marking the recently moved thumb (and thus the current video position)
    const [lastMovedThumbValue, setLastMovedThumbValue] = useState(null)

    // Internationalization for simple strings
    const intl = useIntl();

    /* Special case: If the range slider (video cutting) is used to update the scored scene start/end time,
                     we need to also update the visibility time range safely.
    */
    useEffect(() => {
        const rangeStart = roundNumber(
            initDrawing?.visibilityStart >= visibilityRangeMin && initDrawing?.visibilityStart <= visibilityRangeMax
                ? initDrawing?.visibilityStart
                : visibilityRangeStart
        );

        const rangeEnd = roundNumber(
            initDrawing?.visibilityEnd >= visibilityRangeMin && initDrawing?.visibilityEnd <= visibilityRangeMax
                ? initDrawing?.visibilityEnd
                : visibilityRangeEnd
        );

        setVisibilityRange([rangeStart, rangeEnd]);
        setShapes(initDrawing?.drawing || []);

        // Reset the last moved thumb value, as a new drawing session is created (this causes the "active thumb" slider mark not to be shown)
        setLastMovedThumbValue(null)
    }, [
        initDrawing,
        drawingMode,
        visibilityRangeMin,
        visibilityRangeMax,
        visibilityRangeStart,
        visibilityRangeEnd,
    ]);

    const roundNumber = (number) => {
        return Math.round((number + Number.EPSILON) * 1000) / 1000;
    };

    // Enrich the marks passed as parameter by the standard mark (i.e. the mark to indicate the value of the last moved thumb)
    const getMarks = () => {

        // The thumb has not yet been moved in the current drawing session - return the given marks
        if (lastMovedThumbValue === null) {
            return timeRangeMarks
        }

        // The thumb has already been moved in the current drawing session - extend the given marks
        return [
            ...timeRangeMarks,
            {
                value: lastMovedThumbValue,
                label: <div style={{marginTop: -45}}>
                    <ArrowDropDown/>
                </div>
            }
        ]
    }

    return (
        <div>
            {/* Definition + Rendering of the defined list of shapes */}
            {shapes.map((drawing, index) => (
                <DrawingShape
                    key={index}
                    mode={mode}
                    canvasLeft={canvasLeft}
                    canvasTop={canvasTop}
                    canvasWidth={canvasWidth}
                    canvasHeight={canvasHeight}

                    shapeLeft={drawing.left}
                    shapeTop={drawing.top}
                    shapeWidth={drawing.width}
                    shapeHeight={drawing.height}

                    previewMode={previewMode}
                    drawingMode={drawingMode}
                    shapeUpdated={(shapeTop, shapeLeft, shapeWidth, shapeHeight) => {

                        // The shape of the current index is updated - update the overall list accordingly as well
                        const newShapes = [...shapes];
                        newShapes[index] = {
                            top: roundNumber(shapeTop),
                            left: roundNumber(shapeLeft),
                            width: roundNumber(shapeWidth),
                            height: roundNumber(shapeHeight),
                        };
                        setShapes(newShapes);
                    }}
                    shapeDeleted={() => {

                        // The shape of the current index is deleted
                        const newShapes = [...shapes];
                        newShapes.splice(index, 1);
                        setShapes(newShapes);
                    }}
                />
            ))}

            {/* Draw border around the drawing area */}
            <div className={classes.canvasAreaBorder}
            ></div>

            {/* Toolbar */}
            {drawingMode && (
                <div className={classes.toolbar}>
                    {/* Visibility range slider */}
                    <div className={classes.elementContainer}>
                        <VisibilityRangeSlider
                            onChange={(event, data, activeThumb) => {
                                setVisibilityRange(data)
                                onVisibilityRangeChange(data[activeThumb]) // Sets the current video progress
                                setLastMovedThumbValue(data[activeThumb]) // Sets the last moved mark to the current video progress
                            }}
                            value={visibilityRange}
                            valueLabelDisplay="auto"
                            valueLabelFormat={(value, index) =>
                                moment(value * 1000).format("mm:ss:SS")
                            }
                            min={visibilityRangeMin}
                            max={visibilityRangeMax}
                            step={0.01}
                            disabled={shapes.length === 0}
                            marks={getMarks()}
                        />
                        <span>
              {intl.formatMessage({
                  id: "app.scoredScene.edit.drawing.toolbar.timeRange.label",
                  defaultMessage: "Visibility Time Range",
              })}
            </span>
                    </div>

                    <Divider
                        style={{color: "black", width: 1, height: 50}}
                        orientation={"vertical"}
                    />

                    {/* Add new shape */}
                    <div className={classes.buttonContainer}>
                        <IconButton
                            className={classes.actionButton}
                            onClick={() => {
                                setShapes([
                                    ...shapes,
                                    {
                                        top: 0,
                                        left: 0,
                                        width: 30,
                                        height: 80,
                                    },
                                ]);
                            }}
                            size="large">
                            <Add className={classes.icon}/>
                        </IconButton>
                        <span>
              {intl.formatMessage({
                  id: "app.scoredScene.edit.drawing.toolbar.add.label",
                  defaultMessage: "Add",
              })}
            </span>
                    </div>

                    {/* Delete all shapes */}
                    {shapes?.length > 0 && (
                        <div className={classes.buttonContainer}>
                            <IconButton
                                className={classes.actionButton}
                                onClick={() => {
                                    setShapes([]);
                                    onDeleteAll();
                                }}
                                size="large">
                                <Delete className={classes.icon}/>
                            </IconButton>
                            <span>
                {intl.formatMessage({
                    id: "app.scoredScene.edit.drawing.toolbar.deleteAll.label",
                    defaultMessage: "Delete All",
                })}
              </span>
                        </div>
                    )}

                    {/* Save + Leave button */}
                    <div className={classes.buttonContainer}>
                        <IconButton
                            className={classes.actionButton}
                            onClick={() => {
                                onSave(
                                    visibilityRange[0],
                                    visibilityRange[1],
                                    shapes
                                );
                                onLeaveDrawingMode();
                            }}
                            size="large">
                            <Check className={classes.icon}/>
                        </IconButton>
                        <span>
              {intl.formatMessage({
                  id: "app.scoredScene.edit.drawing.toolbar.ok.label",
                  defaultMessage: "OK",
              })}
            </span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DrawingSection;
